import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';

import { getOpenedTaskId } from '../../../../../redux/general/selectors';
import { getOpenedTaskWorkspace } from '../../../../../redux/tasks/taskEntities/selectors';
import {
  useGetTaskReminderQuery,
  useCreateOrUpdateReminderMutation,
  useDeleteReminderMutation,
} from '../../../../../services/remindersApi';

const RECURRENCE_OPTIONS = [
  { value: 'none', label: "Doesn't repeat" },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const PRESETS = [
  {
    label: '30 min',
    getDate: () => {
      const d = new Date();
      d.setMinutes(d.getMinutes() + 30, 0, 0);
      return d;
    },
  },
  {
    label: '1 hour',
    getDate: () => {
      const d = new Date();
      d.setHours(d.getHours() + 1, 0, 0, 0);
      return d;
    },
  },
  {
    label: '3 hours',
    getDate: () => {
      const d = new Date();
      d.setHours(d.getHours() + 3, 0, 0, 0);
      return d;
    },
  },
];

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));
const ITEM_H = 36;
const VISIBLE_ITEMS = 5;
const POPOVER_WIDTH = 300;
const POPOVER_HEIGHT_EST = 540;

const roundUpToNearest5 = (date) => {
  const m = Math.ceil(date.getMinutes() / 5) * 5;
  const d = new Date(date);
  d.setSeconds(0, 0);
  d.setMinutes(m % 60);
  if (m >= 60) d.setHours(d.getHours() + 1);
  return d;
};

const formatReminderDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const TimeColumn = ({ items, value, onChange }) => {
  const selectedRef = useRef(null);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'center', behavior: 'instant' });
  }, [value]);

  return (
    <div style={{ height: ITEM_H * VISIBLE_ITEMS, overflowY: 'auto' }} className="flex flex-col">
      {items.map((item) => (
        <div
          key={item}
          ref={item === value ? selectedRef : null}
          onClick={() => onChange(item)}
          style={{ height: ITEM_H, minHeight: ITEM_H }}
          className={`flex items-center justify-center cursor-pointer rounded-lg text-[13px] mx-1 select-none transition-colors
            ${item === value
              ? 'bg-[rgba(99,102,241,0.1)] text-indigo-600 font-semibold'
              : 'text-gray-500 hover:bg-gray-50'
            }`}
        >
          {item}
        </div>
      ))}
    </div>
  );
};

const TaskDetailsReminder = ({ taskId, workspaceId }) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHour, setSelectedHour] = useState('09');
  const [selectedMinute, setSelectedMinute] = useState('00');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [recurrence, setRecurrence] = useState('none');
  const [note, setNote] = useState('');
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);

  const { data: reminderResp, isLoading } = useGetTaskReminderQuery(taskId, { skip: !taskId });
  const reminder = reminderResp?.data;

  const [createOrUpdate, { isLoading: isSaving }] = useCreateOrUpdateReminderMutation();
  const [deleteReminder, { isLoading: isDeleting }] = useDeleteReminderMutation();

  useEffect(() => {
    if (!open) return;
    if (reminder) {
      const d = dayjs(reminder.remindAt);
      setSelectedDate(d);
      setSelectedHour(d.format('HH'));
      setSelectedMinute(String(Math.round(d.minute() / 5) * 5 % 60).padStart(2, '0'));
      setRecurrence(reminder.recurrence || 'none');
      setNote(reminder.note || '');
      setSelectedPreset(null);
    } else {
      setSelectedDate(null);
      setSelectedHour('09');
      setSelectedMinute('00');
      setRecurrence('none');
      setNote('');
      setSelectedPreset(null);
    }
  }, [reminder, open]);

  const handlePreset = useCallback((preset) => {
    const d = roundUpToNearest5(preset.getDate());
    setSelectedDate(dayjs(d));
    setSelectedHour(String(d.getHours()).padStart(2, '0'));
    setSelectedMinute(String(d.getMinutes()).padStart(2, '0'));
    setSelectedPreset(preset.label);
  }, []);

  const getRemindAtDate = useCallback(() => {
    if (!selectedDate) return null;
    const d = selectedDate.toDate();
    d.setHours(parseInt(selectedHour, 10), parseInt(selectedMinute, 10), 0, 0);
    return d;
  }, [selectedDate, selectedHour, selectedMinute]);

  const remindAtDate = getRemindAtDate();
  const isPast = !!remindAtDate && remindAtDate <= new Date();

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow >= POPOVER_HEIGHT_EST
      ? rect.bottom + 6
      : rect.top - POPOVER_HEIGHT_EST - 6;
    const left = Math.min(
      rect.right - POPOVER_WIDTH,
      window.innerWidth - POPOVER_WIDTH - 8,
    );
    setPopoverPos({ top: Math.max(8, top), left: Math.max(8, left) });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (e.target.closest('.ant-picker-dropdown')) return;
      if (e.target.closest('.ant-select-dropdown')) return;
      if (
        popoverRef.current && !popoverRef.current.contains(e.target) &&
        buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleSave = useCallback(async () => {
    const d = getRemindAtDate();
    if (!d || d <= new Date()) return;
    await createOrUpdate({ taskId, workspaceId, remindAt: d.toISOString(), recurrence, note });
    setOpen(false);
  }, [createOrUpdate, taskId, workspaceId, getRemindAtDate, recurrence, note]);

  const handleDelete = useCallback(async () => {
    if (!reminder?._id) return;
    await deleteReminder({ reminderId: reminder._id, taskId });
    setOpen(false);
  }, [deleteReminder, reminder, taskId]);

  if (!taskId) return null;

  return (
    <div className="flex items-center py-2.5 pr-2">
      <div className="w-32 shrink-0 flex items-center gap-2">
        <img src="/images/icons/alarm-gray.svg" alt="" className="w-4 h-4 shrink-0 opacity-50" />
        <span className="text-[13px] text-[rgba(15,15,25,0.5)] font-medium">
          {intl.formatMessage({ id: 'REMINDER', defaultMessage: 'Reminder' })}
        </span>
      </div>

      <div className="flex-1 flex justify-end">
        <button
          ref={buttonRef}
          onClick={() => setOpen((v) => !v)}
          className="text-[13px] px-2 py-1 rounded-lg transition-colors"
          style={
            reminder
              ? { color: '#6366f1', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)' }
              : { color: 'rgba(15,15,25,0.35)', background: 'transparent', border: '1px solid transparent' }
          }
          onMouseEnter={(e) => { if (!reminder) e.currentTarget.style.background = 'rgba(99,102,241,0.05)'; }}
          onMouseLeave={(e) => { if (!reminder) e.currentTarget.style.background = 'transparent'; }}
        >
          {isLoading
            ? '…'
            : reminder
            ? formatReminderDate(reminder.snoozedTo || reminder.remindAt)
            : intl.formatMessage({ id: 'SET_REMINDER', defaultMessage: 'Set reminder' })}
        </button>
      </div>

      {open && createPortal(
        <div
          ref={popoverRef}
          className="bg-white rounded-xl border border-[#eee] p-4"
          style={{
            position: 'fixed',
            top: popoverPos.top,
            left: popoverPos.left,
            width: POPOVER_WIDTH,
            zIndex: 9999,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            colorScheme: 'light',
          }}
        >
          <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3">
            {intl.formatMessage({ id: 'REMINDER', defaultMessage: 'Reminder' })}
          </div>

          {/* Quick presets */}
          <div className="flex gap-1.5 mb-4">
            {PRESETS.map((preset) => {
              const active = selectedPreset === preset.label;
              return (
                <button
                  key={preset.label}
                  onClick={() => handlePreset(preset)}
                  className="px-2.5 py-1 rounded-lg text-[12px] font-medium transition-colors"
                  style={
                    active
                      ? { background: 'rgba(99,102,241,0.12)', color: '#6366f1', border: '1px solid rgba(99,102,241,0.3)' }
                      : { background: 'rgba(0,0,0,0.04)', color: 'rgba(15,15,25,0.65)', border: '1px solid transparent' }
                  }
                >
                  {preset.label}
                </button>
              );
            })}
          </div>

          {/* Date */}
          <div className="mb-3">
            <label className="block text-[12px] text-gray-500 mb-1.5">
              {intl.formatMessage({ id: 'DATE', defaultMessage: 'Date' })}
            </label>
            <DatePicker
              value={selectedDate}
              onChange={(date) => {
                setSelectedDate(date || null);
                setSelectedPreset(null);
              }}
              disabledDate={(d) => d && d < dayjs().startOf('day')}
              allowClear={false}
              showToday={false}
              style={{ width: '100%', borderColor: '#e5e7eb', borderRadius: 8, fontSize: 13 }}
              popupStyle={{ zIndex: 10001 }}
              getPopupContainer={() => document.body}
              renderExtraFooter={() => (
                <div className="flex justify-between items-center px-3 py-1">
                  <span
                    onClick={() => { setSelectedDate(dayjs()); setSelectedPreset(null); }}
                    className="cursor-pointer text-[12px] text-indigo-500 hover:underline"
                  >
                    {intl.formatMessage({ id: 'TODAY', defaultMessage: 'Today' })}
                  </span>
                  <span
                    onClick={() => { setSelectedDate(null); setSelectedPreset(null); }}
                    className="cursor-pointer text-[12px] text-red-400 hover:underline"
                  >
                    {intl.formatMessage({ id: 'CLEAR', defaultMessage: 'Clear' })}
                  </span>
                </div>
              )}
            />
          </div>

          {/* Time — two scrollable columns (MUI digital clock style) */}
          <div className="mb-4">
            <label className="block text-[12px] text-gray-500 mb-1.5">
              {intl.formatMessage({ id: 'TIME', defaultMessage: 'Time' })}
            </label>
            <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
              <div className="flex-1 border-r border-gray-100">
                <TimeColumn
                  items={HOURS}
                  value={selectedHour}
                  onChange={(h) => { setSelectedHour(h); setSelectedPreset(null); }}
                />
              </div>
              <div className="flex-1">
                <TimeColumn
                  items={MINUTES}
                  value={selectedMinute}
                  onChange={(m) => { setSelectedMinute(m); setSelectedPreset(null); }}
                />
              </div>
            </div>
          </div>

          {isPast && (
            <div className="text-[11px] text-red-400 mb-2">
              {intl.formatMessage({ id: 'REMINDER_PAST_WARNING', defaultMessage: 'Choose a future date and time' })}
            </div>
          )}

          {/* Recurrence */}
          <div className="mb-3">
            <label className="block text-[12px] text-gray-500 mb-1">
              {intl.formatMessage({ id: 'RECURRENCE', defaultMessage: 'Recurrence' })}
            </label>
            <Select
              value={recurrence}
              onChange={(val) => setRecurrence(val)}
              options={RECURRENCE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              style={{ width: '100%', fontSize: 13 }}
              popupStyle={{ zIndex: 10001 }}
              getPopupContainer={() => document.body}
            />
          </div>

          {/* Note */}
          <div className="mb-4">
            <label className="block text-[12px] text-gray-500 mb-1">
              {intl.formatMessage({ id: 'NOTE_OPTIONAL', defaultMessage: 'Note (optional)' })}
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={intl.formatMessage({ id: 'REMINDER_NOTE_PLACEHOLDER', defaultMessage: 'Add a note…' })}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:border-indigo-400"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={!selectedDate || isPast || isSaving}
              className="flex-1 py-2 rounded-lg text-[13px] font-semibold text-white disabled:opacity-50"
              style={{ background: '#6366f1' }}
            >
              {isSaving
                ? intl.formatMessage({ id: 'SAVING', defaultMessage: 'Saving…' })
                : intl.formatMessage({ id: 'SAVE', defaultMessage: 'Save' })}
            </button>
            {reminder && (
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-3 py-2 rounded-lg text-[13px] font-medium text-red-500 border border-red-100 hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                {intl.formatMessage({ id: 'REMOVE', defaultMessage: 'Remove' })}
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              className="px-3 py-2 rounded-lg text-[13px] text-gray-400 hover:text-gray-600 transition-colors"
            >
              {intl.formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' })}
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  taskId: getOpenedTaskId(state),
  workspaceId: getOpenedTaskWorkspace(state),
});

export default connect(mapStateToProps)(TaskDetailsReminder);
